import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { ChecklistItem } from "store/apis/financialClosing";
import { CheckListUpdatePayload } from "../CheckList";
import { useFormikContext } from "formik";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { debounce } from "utils/debouncing";
import {
  CHECKLIST_ITEM_TYPE,
  DOCUMENT_STATUS,
} from "constants/financialClosing";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useModal } from "hooks/useModal";
import { useCallback } from "react";

export const ChecklistItemV0 = ({
  item,
  index,
  onCheckListChange,
  getReportLinks,
  isFormEditable,
  previewModal,
  entityId,
  folderPath,
}: {
  item: ChecklistItem;
  index: number;
  onCheckListChange: (arg: CheckListUpdatePayload) => Promise<void>;
  getReportLinks: (type: ChecklistItem["type"]) => string;
  isFormEditable: boolean;
  previewModal: ReturnType<typeof useModal>;
  entityId: string;
  folderPath: string[];
}) => {
  const { setFieldValue, errors, values } = useFormikContext<{
    checklist_items: ChecklistItem[];
  }>();
  const { downloadFile } = useDownloadFile();

  const isCashFlowStatement =
    item.type === CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT;

  // Helper function to check if an item has validation errors
  const hasItemError = (itemUuid: string) => {
    if (!errors.checklist_items || !Array.isArray(errors.checklist_items)) {
      return false;
    }

    const itemIndex = values.checklist_items.findIndex(
      (item) => item.uuid === itemUuid
    );
    return itemIndex >= 0 && Boolean(errors.checklist_items[itemIndex]);
  };

  const handleFileUpload = useCallback(
    async (files: FileType[]) => {
      if (!isFormEditable) return;

      let uploadedFile = files[0];

      if ("uuid" in uploadedFile) {
        await onCheckListChange({
          checklistItemId: item.uuid,
          document_id: uploadedFile.uuid as string,
          document_type: DOCUMENT_STATUS.UPLOAD,
        });
      }

      setFieldValue(
        `checklist_items[${index}].file_data`,
        uploadedFile as File
      );
    },
    [isFormEditable, item.uuid, index, onCheckListChange, setFieldValue]
  );

  const showError = hasItemError(item.uuid);

  return (
    <div className="t-flex t-flex-col t-gap-4 t-pb-2">
      <div className="t-text-body-sm">
        <RadioGroup.Root
          className="t-flex t-justify-between"
          onValueChange={(e) => {
            if (e === "GENERATE") {
              isFormEditable &&
                onCheckListChange({
                  checklistItemId: item.uuid,
                  document_type: e,
                });
            } else {
              isFormEditable &&
                onCheckListChange({
                  checklistItemId: item.uuid,
                  document_type: e,
                });
            }
          }}
          defaultValue={item.document_type || "NONE"}
          disabled={!isFormEditable}
        >
          <div className="t-flex t-gap-3">
            {isCashFlowStatement && (
              <span>
                <RadioGroup.Item id={`${item.uuid}NONE`} asChild value="NONE">
                  <span className="-t-ml-1">Not needed</span>
                </RadioGroup.Item>
              </span>
            )}
            <span>
              <RadioGroup.Item
                id={`${item.uuid}_GENERATE`}
                asChild
                value="GENERATE"
              >
                <span className="-t-ml-1">Generate</span>
              </RadioGroup.Item>
            </span>
            <span>
              <RadioGroup.Item
                id={`${item.uuid}_UPLOAD`}
                asChild
                value="UPLOAD"
                className="t-gap-0"
              >
                <span className="-t-ml-1">Upload file</span>
              </RadioGroup.Item>
            </span>
            <span>
              <RadioGroup.Item id={`${item.uuid}_LINK`} asChild value="LINK">
                <span className="-t-ml-1">Add link</span>
              </RadioGroup.Item>
            </span>
          </div>
        </RadioGroup.Root>
      </div>
      {item.document_type === "GENERATE" && (
        <div className="t-flex t-flex-col t-gap-2">
          <div className="t-text-body-sm t-text-text-30">
            This link opens live financials with the financial closing period
            pre-selected on Inkle
          </div>
          <BareInput defaultValue={item.file_url || ""} disabled />
        </div>
      )}
      {item.document_type === "UPLOAD" && (
        <div className="t-flex t-flex-col t-gap-1">
          <FileInput
            allowUploadToFolder
            withInkleDocs
            block
            entityId={entityId}
            folderPath={folderPath}
            name={`checklist_items[${index}].file_data`}
            file={
              item.file_data
                ? {
                    name: item.file_data.name,
                    file_type: item.file_data.file_type || item.file_data.type,
                    is_previewable: item.file_data.is_previewable,
                    uuid: item.file_data.uuid,
                  }
                : undefined
            }
            {...(isFormEditable && {
              onDelete: () => {
                onCheckListChange({
                  checklistItemId: item.uuid,
                  delete_document: true,
                });
                setFieldValue(`checklist_items[${index}].file_data`, undefined);
              },
            })}
            onFileClick={previewModal.open}
            onDrop={handleFileUpload}
            onDownload={
              item.file_data?.uuid && item.file_data?.group_id
                ? downloadFile.bind(null, {
                    file_id: item.file_data.uuid,
                    group_id: item.file_data.group_id,
                  })
                : undefined
            }
            label="Upload file"
            disabled={!isFormEditable}
            type={showError ? "error" : "default"}
          />
          {showError && (
            <div className="t-text-body-sm-italic t-italic t-text-red">
              This field is required
            </div>
          )}
        </div>
      )}
      {item.document_type === "LINK" && (
        <div className="t-flex t-flex-col t-gap-2">
          <div className="t-text-body-sm t-text-text-30">Add link</div>
          <BareInput
            customType={showError ? "error" : "success"}
            defaultValue={item.file_url}
            onChange={debounce((e) => {
              const value = e.target.value;
              onCheckListChange({
                checklistItemId: item.uuid,
                file_url: value,
              });
              setFieldValue(
                `checklist_items[${index}].file_url`,
                !item.resolved_by_admin
              );
            })}
            label="Add link"
            placeholder="Provide a URL to an external document"
            disabled={!isFormEditable}
            required
          />
          {showError && (
            <div className="t-text-body-sm-italic t-italic t-text-red">
              This field is required
            </div>
          )}
        </div>
      )}
    </div>
  );
};
